import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { InstagramFloat } from "@/components/InstagramFloat";
import { SkoolPromoBanner } from "@/components/SkoolPromoBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { useState, lazy, Suspense, ComponentType } from "react";
import { CssSpinner } from "@/components/CssSpinner";
import Index from "./pages/Index";

// Wrap lazy imports to auto-reload on stale chunk errors (after a deploy)
const lazyWithRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message || err);
      if (
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module")
      ) {
        if (!sessionStorage.getItem("chunk-reload")) {
          sessionStorage.setItem("chunk-reload", "1");
          window.location.reload();
        }
      }
      throw err;
    }
  });

// Lazy load pages for better performance (Index is eager — it's the LCP page)
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const About = lazyWithRetry(() => import("./pages/About"));
const TemplateDetail = lazyWithRetry(() => import("./pages/TemplateDetail"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const Newsletter = lazyWithRetry(() => import("./pages/Newsletter"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const Prompts = lazyWithRetry(() => import("./pages/Prompts"));
const Packs = lazyWithRetry(() => import("./pages/Packs"));
const Classroom = lazyWithRetry(() => import("./pages/Classroom"));
const ClassroomLesson = lazyWithRetry(() => import("./pages/ClassroomLesson"));
const CommunityResources = lazyWithRetry(() => import("./pages/CommunityResources"));
const CommunityResourcePost = lazyWithRetry(() => import("./pages/CommunityResourcePost"));
const PackDetail = lazyWithRetry(() => import("./pages/PackDetail"));
const N8n = lazyWithRetry(() => import("./pages/N8n"));
const VideosHome = lazyWithRetry(() => import("./pages/videos/VideosHome"));
const VideosTrending = lazyWithRetry(() => import("./pages/videos/Trending"));
const VideosLatest = lazyWithRetry(() => import("./pages/videos/Latest"));
const VideosWeekly = lazyWithRetry(() => import("./pages/videos/Weekly"));
const VideosCategory = lazyWithRetry(() => import("./pages/videos/Category"));
const VideosChannel = lazyWithRetry(() => import("./pages/videos/Channel"));
const N8nTemplates = lazyWithRetry(() => import("./pages/N8nTemplates"));
const WorkflowTemplate = lazyWithRetry(() => import("./pages/WorkflowTemplate"));
const N8nVsMake = lazyWithRetry(() => import("./pages/N8nVsMake"));
const AiAutomation = lazyWithRetry(() => import("./pages/AiAutomation"));
const AiAgencyRoadmap = lazyWithRetry(() => import("./pages/AiAgencyRoadmap"));
const AiAutomationRoadmap = lazyWithRetry(() => import("./pages/AiAutomationRoadmap"));
const AutomationMoneyMaking = lazyWithRetry(() => import("./pages/AutomationMoneyMaking"));
const BuildFreeAiAgencyWebsite = lazyWithRetry(() => import("./pages/BuildFreeAiAgencyWebsite"));
const LaunchYourAiAgency = lazyWithRetry(() => import("./pages/LaunchYourAiAgency"));
const BestAiAutomationTools = lazyWithRetry(() => import("./pages/BestAiAutomationTools"));
const ImportTemplates = lazyWithRetry(() => import("./pages/ImportTemplates"));
const AdminLayout = lazyWithRetry(() => import("./pages/admin/AdminLayout"));
const AdminUsersPage = lazyWithRetry(() => import("./pages/admin/AdminUsersPage"));
const AdminTrafficPage = lazyWithRetry(() => import("./pages/admin/AdminTrafficPage"));
const AdminResourcesPage = lazyWithRetry(() => import("./pages/admin/AdminResourcesPage"));
const AdminSalesPage = lazyWithRetry(() => import("./pages/admin/AdminSalesPage"));
const AdminWorkflowsPage = lazyWithRetry(() => import("./pages/admin/AdminWorkflowsPage"));
const AdminPromptsPage = lazyWithRetry(() => import("./pages/admin/AdminPromptsPage"));
const AdminPacksPage = lazyWithRetry(() => import("./pages/admin/AdminPacksPage"));
const ResourceDetail = lazyWithRetry(() => import("./pages/ResourceDetail"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const AuthConfirm = lazyWithRetry(() => import("./pages/AuthConfirm"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const PaymentCancelled = lazyWithRetry(() => import("./pages/PaymentCancelled"));
const Courses = lazyWithRetry(() => import("./pages/Courses"));
const CourseDetail = lazyWithRetry(() => import("./pages/CourseDetail"));
const CategoryPage = lazyWithRetry(() => import("./pages/CategoryPage"));
const AiDeveloper = lazyWithRetry(() => import("./pages/AiDeveloper"));
const WhiteLabel = lazyWithRetry(() => import("./pages/WhiteLabel"));
const N8n400Workflows = lazyWithRetry(() => import("./pages/n8n-templates/N8n400Workflows"));
const N8n15kLibrary = lazyWithRetry(() => import("./pages/n8n-templates/N8n15kLibrary"));
const N8n250VideoGuides = lazyWithRetry(() => import("./pages/n8n-templates/N8n250VideoGuides"));
const N8n50Marketing = lazyWithRetry(() => import("./pages/n8n-templates/N8n50Marketing"));
const ClaudeCode = lazyWithRetry(() => import("./pages/blogs/ClaudeCode"));
const AntiGravity = lazyWithRetry(() => import("./pages/blogs/AntiGravity"));
const N8nAutomation = lazyWithRetry(() => import("./pages/blogs/N8nAutomation"));
const ClaudeSkills = lazyWithRetry(() => import("./pages/blogs/ClaudeSkills"));
const AiIncomeSystemPage = lazyWithRetry(() => import("./pages/AiIncomeSystem"));
const StartHere = lazyWithRetry(() => import("./pages/StartHere"));
const Apps = lazyWithRetry(() => import("./pages/Apps"));
const PromptMaker = lazyWithRetry(() => import("./pages/apps/PromptMaker"));
const BrandPackApp = lazyWithRetry(() => import("./pages/apps/BrandPack"));
const AdminPromptMakerPage = lazyWithRetry(() => import("./pages/admin/AdminPromptMakerPage"));

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <CssSpinner size={48} />
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const hideChrome = false;
  useVisitorTracking();

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background relative">
        {!hideChrome && <Navbar onSearchClick={() => setIsSearchOpen(true)} />}
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            {/* Scanner-safe magic-link landing page — see src/pages/AuthConfirm.tsx */}
            <Route path="/auth/confirm" element={<AuthConfirm />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminUsersPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="traffic" element={<AdminTrafficPage />} />
              <Route path="resources" element={<AdminResourcesPage />} />
              <Route path="sales" element={<AdminSalesPage />} />
              <Route path="workflows" element={<AdminWorkflowsPage />} />
              <Route path="prompts" element={<AdminPromptsPage />} />
              <Route path="packs" element={<AdminPacksPage />} />
              <Route path="prompt-maker" element={<AdminPromptMakerPage />} />
            </Route>
            <Route path="/resources/:id" element={<ResourceDetail />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            {/* Protected Routes */}
            <Route path="/templates/:slug" element={<ProtectedRoute><TemplateDetail /></ProtectedRoute>} />
            <Route path="/category/:categorySlug" element={<ProtectedRoute><CategoryPage /></ProtectedRoute>} />
            {/* /about is public and always was supposed to be. /about-us and /contact
                both sat behind ProtectedRoute, so every crawler and every stranger hit
                the login wall on the two pages a stranger most needs. */}
            <Route path="/about" element={<About />} />
            <Route path="/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/contact" element={<Navigate to="/about" replace />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/newsletter" element={<Newsletter />} />
            <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/n8n" element={<ProtectedRoute><N8n /></ProtectedRoute>} />
            <Route path="/videos" element={<ProtectedRoute><VideosHome /></ProtectedRoute>} />
            <Route path="/videos/trending" element={<ProtectedRoute><VideosTrending /></ProtectedRoute>} />
            <Route path="/videos/latest" element={<ProtectedRoute><VideosLatest /></ProtectedRoute>} />
            <Route path="/videos/weekly" element={<ProtectedRoute><VideosWeekly /></ProtectedRoute>} />
            <Route path="/videos/category/:slug" element={<ProtectedRoute><VideosCategory /></ProtectedRoute>} />
            <Route path="/videos/channel/:slug" element={<ProtectedRoute><VideosChannel /></ProtectedRoute>} />
            <Route path="/n8n-templates" element={<ProtectedRoute><N8nTemplates /></ProtectedRoute>} />
            <Route path="/workflow-template" element={<ProtectedRoute><WorkflowTemplate /></ProtectedRoute>} />
            <Route path="/n8n-vs-make" element={<ProtectedRoute><N8nVsMake /></ProtectedRoute>} />
            <Route path="/ai-automation" element={<ProtectedRoute><AiAutomation /></ProtectedRoute>} />
            <Route path="/ai-agency-roadmap" element={<ProtectedRoute><AiAgencyRoadmap /></ProtectedRoute>} />
            <Route path="/ai-automation-roadmap" element={<ProtectedRoute><AiAutomationRoadmap /></ProtectedRoute>} />
            <Route path="/automation-money-making" element={<ProtectedRoute><AutomationMoneyMaking /></ProtectedRoute>} />
            <Route path="/build-free-ai-agency-website" element={<ProtectedRoute><BuildFreeAiAgencyWebsite /></ProtectedRoute>} />
            <Route path="/launch-your-ai-agency" element={<ProtectedRoute><LaunchYourAiAgency /></ProtectedRoute>} />
            <Route path="/best-ai-automation-tools" element={<ProtectedRoute><BestAiAutomationTools /></ProtectedRoute>} />
            <Route path="/import-templates" element={<ProtectedRoute><ImportTemplates /></ProtectedRoute>} />
            <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
            <Route path="/prompts" element={<ProtectedRoute><Prompts /></ProtectedRoute>} />
            {/* Build Packs: the pages are public (write-up = SEO teaser); the files are
                gated by a storage policy, so no ProtectedRoute here on purpose. */}
            <Route path="/packs" element={<Packs />} />
            <Route path="/packs/:slug" element={<PackDetail />} />
            {/* Classroom Library: public index; bodies + files gated by RLS/storage policy. */}
            <Route path="/classroom" element={<Classroom />} />
            <Route path="/classroom/:slug" element={<ClassroomLesson />} />
            {/* Community Resource: public index of the Skool community category (bodies/images/files gated). */}
            <Route path="/community-resources" element={<CommunityResources />} />
            <Route path="/community-resources/:slug" element={<CommunityResourcePost />} />
            {/* Vista previa solo en desarrollo: permite revisar el diseño sin sesión.
                import.meta.env.DEV es false en el build, así que nunca llega a producción. */}
            {import.meta.env.DEV && (
              <Route path="/prompts-preview" element={<Prompts />} />
            )}
            <Route path="/courses/:courseId" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
            {/* Hire-Dev / White-Label hidden pending Stripe migration — redirect to home to avoid stale Whop checkout */}
            <Route path="/ai-developer" element={<Navigate to="/" replace />} />
            <Route path="/white-label" element={<Navigate to="/" replace />} />
            {/* Previous-owner content: a Monday "Global Agentic AI Live Webinar" with a
                WhatsApp group and career guidance, none of which runs. public/_redirects
                serves a real 301 at the edge; these keep client-side navigation honest. */}
            <Route path="/webinar" element={<Navigate to="/start-here" replace />} />
            <Route path="/webinar/success" element={<Navigate to="/start-here" replace />} />
            <Route path="/start-here" element={<StartHere />} />
            {/* Members Apps: public landing that sells the upgrade; the apps check access themselves. */}
            <Route path="/apps" element={<Apps />} />
            {/* Gate is inside the page (paid + Skool Premium/VIP) and again in the edge function. */}
            <Route path="/apps/prompt-maker" element={<PromptMaker />} />
            <Route path="/apps/brand-pack" element={<BrandPackApp />} />
            <Route path="/n8n-templates/400-workflows" element={<ProtectedRoute><N8n400Workflows /></ProtectedRoute>} />
            <Route path="/n8n-templates/15k-library" element={<ProtectedRoute><N8n15kLibrary /></ProtectedRoute>} />
            <Route path="/n8n-templates/250-video-guides" element={<ProtectedRoute><N8n250VideoGuides /></ProtectedRoute>} />
            <Route path="/n8n-templates/50-marketing" element={<ProtectedRoute><N8n50Marketing /></ProtectedRoute>} />
            <Route path="/claude-code" element={<ProtectedRoute><ClaudeCode /></ProtectedRoute>} />
            <Route path="/antigravity" element={<ProtectedRoute><AntiGravity /></ProtectedRoute>} />
            <Route path="/n8n-automation" element={<ProtectedRoute><N8nAutomation /></ProtectedRoute>} />
            <Route path="/claude-skills" element={<ProtectedRoute><ClaudeSkills /></ProtectedRoute>} />
            <Route path="/ai-income-system" element={<ProtectedRoute><AiIncomeSystemPage /></ProtectedRoute>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            {/* Was wrapped in ProtectedRoute, which turned every typo and every dead
                link into a redirect to the login page instead of a 404. That is a soft
                404: search engines see 200 + a login form on infinite URLs. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        {!hideChrome && <Footer />}
        {!hideChrome && <InstagramFloat />}
      </div>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
